/**
 * Which navigation entries a user may see.
 *
 * Two things decide it besides the entry's own switches. An entry marked `adminOnly` is for an
 * administrator alone. Every other entry is open to an administrator, and to anyone else only when
 * their user category lists the page (`allowedPages`, set by the administrator). A page the user may
 * not open is hidden from the menu, not shown greyed out, so a restriction never reads as a broken
 * page.
 *
 * The administrator test is deliberately the **exact** comparison the backend makes
 * (`ADMIN_ROLE = 'ADMIN'` in `domain/hardwareServiceReleases/releaseModel.ts`), not a lenient
 * case-insensitive one. The menu must hide precisely what the API would refuse to serve: a looser
 * test here would put the entry back in front of someone the server still says no to, which is the
 * defect this rule exists to remove.
 *
 * Hiding a menu entry is a courtesy, not the security boundary: the administrator-only screens that
 * decide these lists are enforced by the API, and the page guard (`PageAccessGuard`) turns away a
 * page typed into the address bar.
 */

import { type ModuleState, type ModuleUnavailableReason, moduleUnavailableReason } from './modules';
import { isPageAllowed, isPermissionable, pageKeyOf, routePageKey } from './pagePermissions';

/** The role name the backend treats as an administrator. */
export const ADMIN_ROLE = 'ADMIN';

/** Who is looking at the menu: their role, and the pages their user category lets them open. */
export interface MenuAudience {
    role: string | null | undefined;
    /**
     * `null` for no restriction (an administrator). A list is exactly the pages that may be opened.
     * `undefined` means the answer has not arrived, which is treated as no pages: until the server
     * has said what a user may see, they are shown nothing rather than everything.
     */
    allowedPages: readonly string[] | null | undefined;
}

/** The fields a navigation entry must carry for its visibility to be decided. */
export interface MenuVisibilityFields {
    /** The entry's route, e.g. `/pred_casa`. Its page key is this without the leading slash. */
    path: string;
    visible: boolean;
    enabled: boolean;
    /** When true, only an administrator sees the entry. Absent means the user category decides. */
    adminOnly?: boolean;
    /**
     * The separately sold module the entry belongs to. Such an entry is never hidden for being
     * unavailable: it is shown greyed out instead, as a hint that the module exists.
     */
    moduleKey?: string;
}

/** An entry the caller may see, and why it is greyed out, if it is. */
export interface PresentedMenuItem<T> {
    item: T;
    /** `null` when the entry can be used; otherwise the reason its module is unavailable. */
    disabledReason: ModuleUnavailableReason | null;
}

/** True when the caller holds the administrator role. */
export function isAdminRole(role: string | null | undefined): boolean {
    return role === ADMIN_ROLE;
}

/**
 * True when `audience` should see `item`.
 *
 * `visible` and `enabled` are the entry's own switches and are checked first: an entry turned off
 * in configuration stays off for an administrator too.
 */
export function isMenuItemVisibleTo(item: MenuVisibilityFields, audience: MenuAudience): boolean {
    if (!item.visible || !item.enabled) {
        return false;
    }

    if (isAdminRole(audience.role)) {
        return true;
    }

    if (item.adminOnly === true) {
        return false;
    }

    return isPageAllowed(audience.allowedPages, pageKeyOf(item.path));
}

/**
 * True when `audience` may open the page at `pathname`.
 *
 * The page guard asks this, so a page typed into the address bar is refused as the menu would have
 * hidden it. An address that is not a menu page at all (the home page, Clients, a retired route) is
 * open: what is restricted is exactly what the menu lists, no more.
 */
export function isRouteAllowed<T extends MenuVisibilityFields>(
    pathname: string,
    audience: MenuAudience,
    items: readonly T[],
): boolean {
    const pageKey = routePageKey(pathname);
    const item = items.find(entry => pageKeyOf(entry.path) === pageKey);

    if (item === undefined || isAdminRole(audience.role)) {
        return true;
    }

    // An admin-only page is not something a category grants, so the guard leaves it to the page:
    // the workstation page explains itself to a non-administrator who arrives by address, which
    // was decided to be better than a refusal (see hardware-service-download.spec.ts).
    return !isPermissionable(item) || isPageAllowed(audience.allowedPages, pageKey);
}

/** The entries `audience` should see, in their original order. */
export function visibleMenuItems<T extends MenuVisibilityFields>(
    items: readonly T[],
    audience: MenuAudience,
): T[] {
    return items.filter(item => isMenuItemVisibleTo(item, audience));
}

/**
 * The entries `audience` should see, each marked usable or greyed out.
 *
 * Visibility is decided first, by {@link isMenuItemVisibleTo}, so a module entry the user may not
 * open stays hidden. Only then is a module entry's availability looked up in `modules`.
 */
export function presentMenuItems<T extends MenuVisibilityFields>(
    items: readonly T[],
    audience: MenuAudience,
    modules: readonly ModuleState[],
): PresentedMenuItem<T>[] {
    return visibleMenuItems(items, audience).map(item => ({
        item,
        disabledReason:
            item.moduleKey === undefined ? null : moduleUnavailableReason(modules, item.moduleKey),
    }));
}
